import React, {useContext, useMemo, useState} from "react";
import "./ContentAdminUsers.scss"
import {getIdWithToken} from "../../../../utils/storage/Token";
import {DeleteUser, useFetchAllUsers} from "../../../../service/AdminService";
import FCSpinner from "../../../FCObjets/FCSpinner";
import {AdminUserInterface} from "../../../../service/Interfaces/AdminInterface";
import FCTag from "../../../FCObjets/FCTag";
import StatusAccountUser from "../../../../utils/StatusAccountUser";
import FCAlertMsgErrorAPI from "../../../FCObjets/FCAlertMsg/FCAlertMsgErrorAPI";
import FCContentModalFL, {DataForm} from "../../../FCObjets/Form/FCContentModalFL";
import {TypeForm} from "../../../../types/EnumTypeForm";
import {EventUpdatedContext} from "../../../../store/EventUpdatedContext";

type FilterTab = "Tous les membres" | "Administrateurs" | "Guest";

/** Mapping onglet → valeur role BDD (lowercase) */
const ROLE_FILTER: Record<FilterTab, string | null> = {
    "Tous les membres": null,
    "Administrateurs": "admin",
    "Guest": "guest",
};

/** Couleur d'avatar déterministe basée sur le username */
const AVATAR_COLORS = [
    "#E67E22", "#8E44AD", "#2980B9", "#27AE60",
    "#C0392B", "#16A085", "#D35400", "#1ABC9C"
];
const avatarColor = (username: string): string =>
    AVATAR_COLORS[username.charCodeAt(0) % AVATAR_COLORS.length];

const initials = (u: AdminUserInterface): string => {
    const f = u.firstname?.[0] ?? "";
    const l = u.lastname?.[0] ?? "";
    return (f + l).toUpperCase() || u.username.slice(0, 2).toUpperCase();
};

const ContentAdminUsers: React.FunctionComponent = () => {

    const {cptUpdated, setUpdated} = useContext(EventUpdatedContext);
    const {data: dataUsers, loading, error} = useFetchAllUsers(getIdWithToken(), cptUpdated);

    const [filterTab, setFilterTab] = useState<FilterTab>("Tous les membres");
    const [searchQuery, setSearchQuery] = useState("");

    const [data, setData] = useState<DataForm>({type: TypeForm.EMPTY, data: undefined});

    const gestionModal = (open: boolean, isConfirm: boolean, e: AdminUserInterface | any | undefined) => {
        let t = TypeForm.EMPTY;
        if (open) t = isConfirm ? TypeForm.CONFIRM : TypeForm.ACCOUNT;
        setData({type: t, data: e});
    };

    const filteredUsers = useMemo(() => {
        if (!dataUsers) return [];
        const q = searchQuery.toLowerCase();
        const roleFilter = ROLE_FILTER[filterTab];
        return dataUsers.filter((u: AdminUserInterface) => {
            if (roleFilter && u.role.toLowerCase() !== roleFilter) return false;
            if (q && !u.username.toLowerCase().includes(q) &&
                !u.firstname.toLowerCase().includes(q) &&
                !u.lastname.toLowerCase().includes(q) &&
                !u.contactEmail.toLowerCase().includes(q)) return false;
            return true;
        });
    }, [dataUsers, searchQuery, filterTab]);

    const handleDelete = (u: AdminUserInterface) => {
        const payload = {
            title: `Suppression de ${u.username}`,
            msg: "Êtes-vous sûr de vouloir supprimer cet utilisateur ?",
            onConfirm: () => DeleteUser(getIdWithToken(), u).then(() => setUpdated())
        };
        gestionModal(true, true, payload);
    };

    const FILTER_TABS: FilterTab[] = ["Tous les membres", "Administrateurs", "Guest"];

    return (
        <FCContentModalFL form={data} onModalFormFinished={() => gestionModal(false, false, undefined)}>
            <div className="adminPage">

                {/* Header */}
                <div className="adminHeader">
                    <div className="adminHeaderLeft">
                        <h1>Administration</h1>
                        <p>Gérez les profils utilisateurs et les accès</p>
                    </div>
                    <div className="adminHeaderActions">
                        <div className="adminSearchBar">
                            <span className="material-symbols-outlined">search</span>
                            <input type="text" placeholder="Rechercher un utilisateur..."
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}/>
                        </div>
                    </div>
                </div>

                {loading && <div className="adminLoading"><FCSpinner size="middle"/></div>}
                {error && <FCAlertMsgErrorAPI error={error}/>}

                {dataUsers && (
                    <>
                        {/* KPI */}
                        <div className="adminKpiGrid">
                            <div className="adminKpiCard">
                                <div className="adminKpiLabel">Total utilisateurs</div>
                                <div className="adminKpiValue">{dataUsers.length}</div>
                                <div className="adminKpiEvolution">
                                    <span className="material-symbols-outlined">trending_up</span>
                                    utilisateurs enregistrés
                                </div>
                            </div>
                            <div className="adminKpiCard adminLastLogin">
                                <div className="adminKpiLabel">Dernière création</div>
                                {dataUsers.length > 0 && (() => {
                                    const last = dataUsers[dataUsers.length - 1];
                                    return (
                                        <div className="adminLastLoginUser">
                                            <div className="adminLastLoginAvatar"
                                                 style={{background: `linear-gradient(135deg, ${avatarColor(last.username)}, ${avatarColor(last.username)}aa)`}}>
                                                {initials(last)}
                                            </div>
                                            <div>
                                                <div className="adminLastLoginName">{last.firstname} {last.lastname}</div>
                                                <div className="adminLastLoginTime">@{last.username}</div>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>

                        {/* Filter bar */}
                        <div className="adminFilterBar">
                            <div className="adminFilterTabs">
                                {FILTER_TABS.map((tab) => (
                                    <button key={tab}
                                            className={`adminFilterTab${filterTab === tab ? " active" : ""}`}
                                            onClick={() => setFilterTab(tab)}>
                                        {tab}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Desktop table */}
                        <div className="adminTableWrapper">
                            <table className="adminTable">
                                <thead>
                                <tr>
                                    <th>Nom d'utilisateur</th>
                                    <th>Nom</th>
                                    <th>Prénom</th>
                                    <th>Email</th>
                                    <th>Statut compte</th>
                                    <th>Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filteredUsers.length === 0 && (
                                    <tr>
                                        <td colSpan={6}>
                                            <div className="adminEmptyState">Aucun utilisateur trouvé</div>
                                        </td>
                                    </tr>
                                )}
                                {filteredUsers.map((u: AdminUserInterface, idx: number) => (
                                    <tr key={idx}>
                                        <td>
                                            <div className="adminUserCell">
                                                <div className="adminAvatar"
                                                     style={{background: `linear-gradient(135deg, ${avatarColor(u.username)}, ${avatarColor(u.username)}bb)`}}>
                                                    {initials(u)}
                                                </div>
                                                <span className="adminCellUsername">{u.username}</span>
                                            </div>
                                        </td>
                                        <td><span className="adminCellName">{u.lastname}</span></td>
                                        <td>{u.firstname}</td>
                                        <td><span className="adminCellEmail">{u.contactEmail}</span></td>
                                        <td>
                                            <FCTag value={StatusAccountUser.toString(u.stateAccount)}
                                                   color={StatusAccountUser.toColor(u.stateAccount)}
                                                   style={{fontWeight: "bold", fontSize: "11px"}}/>
                                        </td>
                                        <td>
                                            <div className="adminCellActions">
                                                <button className="adminActionBtn adminActionEdit"
                                                        title="Modifier"
                                                        onClick={() => gestionModal(true, false, u.id)}>
                                                    <span className="material-symbols-outlined">edit</span>
                                                </button>
                                                <button className="adminActionBtn adminActionDelete"
                                                        title="Supprimer"
                                                        onClick={() => handleDelete(u)}>
                                                    <span className="material-symbols-outlined">delete</span>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                            <div className="adminTableFooter">
                                <span className="adminTableCount">
                                    Affichage de {filteredUsers.length} sur {dataUsers.length} utilisateurs
                                </span>
                            </div>
                        </div>

                        {/* Mobile cards */}
                        <div className="adminMobileList">
                            {filteredUsers.length === 0 && (
                                <div className="adminEmptyState">Aucun utilisateur trouvé</div>
                            )}
                            {filteredUsers.map((u: AdminUserInterface, idx: number) => (
                                <div className="adminMobileCard" key={idx}>
                                    <div className="adminMobileCardTop">
                                        <div className="adminAvatar"
                                             style={{
                                                 background: `linear-gradient(135deg, ${avatarColor(u.username)}, ${avatarColor(u.username)}bb)`,
                                                 width: 44, height: 44, borderRadius: 10, fontSize: 15
                                             }}>
                                            {initials(u)}
                                        </div>
                                        <div className="adminMobileCardInfo">
                                            <div className="adminMobileCardUsername">{u.firstname} {u.lastname}</div>
                                            <div className="adminMobileCardEmail">{u.contactEmail}</div>
                                            <FCTag value={StatusAccountUser.toString(u.stateAccount)}
                                                   color={StatusAccountUser.toColor(u.stateAccount)}
                                                   style={{fontWeight: "bold", fontSize: "10px"}}/>
                                        </div>
                                    </div>
                                    <div className="adminMobileCardStatus">
                                        Statut compte : <span>{StatusAccountUser.toString(u.stateAccount)}</span>
                                    </div>
                                    <div className="adminMobileCardActions">
                                        <button className="adminMobileActionBtn edit"
                                                onClick={() => gestionModal(true, false, u.id)}>
                                            <span className="material-symbols-outlined">edit</span>
                                            Modifier
                                        </button>
                                        <button className="adminMobileActionBtn delete"
                                                onClick={() => handleDelete(u)}>
                                            <span className="material-symbols-outlined">delete</span>
                                            Supprimer
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </FCContentModalFL>
    );
};

export default ContentAdminUsers;
